class ShippingMilestoneBar extends HTMLElement {
  connectedCallback() {
    this.markers = Array.from(this.querySelectorAll('.milestone-marker[data-threshold]'))
      .map((marker) => ({
        el: marker,
        threshold: parseInt(marker.dataset.threshold, 10),
      }))
      .filter((marker) => marker.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);

    if (!this.markers.length) return;

    this.messageEl = this.querySelector('.milestone-text');
    this.progressTextEl = this.querySelector('.milestone-progress-text');
    this.completedTextEl = this.querySelector('.milestone-completed-text');
    this.progressBarEl = this.querySelector('.milestone-progress-bar');

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cart) this.syncWithCart(event.cart);
    });

    // The initial width Liquid renders is a best-effort approximation; once
    // layout settles, remeasure against the real marker positions so the
    // fill always lines up with where the icons actually render.
    const initialTotal = parseInt(this.dataset.cartTotal, 10);
    if (!Number.isNaN(initialTotal)) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.progressBarEl) {
            this.progressBarEl.style.width = `${this.calculateProgress(initialTotal)}%`;
          }
        });
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  getCartTotal(cart) {
    const subtotal = Number(cart.items_subtotal_price);
    return subtotal > 0 ? subtotal : Number(cart.total_price || 0);
  }

  // Markers are now placed at fixed, evenly-spaced percentages (see the
  // matching Liquid a1_position/a2_position/a3_position calc), so this is
  // just the formula fallback for when measureMarkerPositions() can't run.
  slotPosition(index, count) {
    if (count <= 0) return 0;
    return ((index + 1) / count) * 100;
  }

  // Marker icons don't necessarily sit at the "clean" fractions slotPosition()
  // assumes — their real position depends on the current CSS layout (flex
  // sizing, padding, label widths, etc). Measuring the actual rendered pixel
  // position keeps the fill glued to the icons regardless of layout details,
  // instead of drifting out of sync whenever the CSS changes.
  measureMarkerPositions() {
    const track = this.querySelector('.milestone-progress-track');
    if (!track) return null;

    const trackRect = track.getBoundingClientRect();
    if (trackRect.width <= 0) return null;

    const positions = this.markers.map((milestone) => {
      const icon = milestone.el.querySelector('.marker-icon') || milestone.el;
      const iconRect = icon.getBoundingClientRect();
      return ((iconRect.left + iconRect.width / 2 - trackRect.left) / trackRect.width) * 100;
    });

    return positions;
  }

  calculateProgress(total) {
    const n = this.markers.length;
    if (!n) return 0;

    const measured = this.measureMarkerPositions();
    const posAt = (index) => (measured ? measured[index] : this.slotPosition(index, n));

    // Once the last tier is reached, fill the whole track edge-to-edge for a
    // clean "fully done" look, rather than stopping at the last marker's
    // measured position (which can sit short of the right edge depending on
    // layout, e.g. flex-centered markers in a short list).
    const last = this.markers[n - 1];
    if (total >= last.threshold) return 100;

    for (let i = 0; i < n; i++) {
      const low = i === 0 ? 0 : this.markers[i - 1].threshold;
      const high = this.markers[i].threshold;
      if (total >= low && total < high) {
        const lowPct = i === 0 ? 0 : posAt(i - 1);
        const highPct = posAt(i);
        const ratio = high === low ? 0 : (total - low) / (high - low);
        const pct = lowPct + ratio * (highPct - lowPct);
        return Math.min(100, Math.max(0, pct));
      }
    }

    return 100;
  }

  syncWithCart(cart) {
    this.hidden = cart.item_count === 0;
    if (cart.item_count === 0) return;

    const total = this.getCartTotal(cart);

    let achievedCount = 0;
    this.markers.forEach((milestone) => {
      if (total >= milestone.threshold) achievedCount += 1;
    });

    // The checkmark is cumulative (every threshold passed), but the reward
    // itself is mutually exclusive — only the single highest-qualifying
    // tier is the one actually applied at checkout.
    const currentIndex = achievedCount - 1;
    let newlyAchieved = false;

    this.markers.forEach((milestone, index) => {
      const achieved = index < achievedCount;
      const isCurrent = index === currentIndex;
      const wasCurrent = milestone.el.classList.contains('current-reward');

      milestone.el.classList.toggle('achieved', achieved);
      milestone.el.classList.toggle('current-reward', isCurrent);

      if (isCurrent && !wasCurrent) {
        newlyAchieved = true;
        milestone.el.classList.add('milestone-pop');
        setTimeout(() => milestone.el.classList.remove('milestone-pop'), 500);
      }
    });

    this.updateMessage(total, achievedCount);
    if (this.progressBarEl) this.progressBarEl.style.width = `${this.calculateProgress(total)}%`;

    if (newlyAchieved && this.dataset.showConfetti === 'true') this.playConfetti();
  }

  // Liquid only renders the header sentence for whichever state the cart
  // was in at page load. When the cart changes afterward via AJAX (no
  // reload), that sentence has to be rebuilt from scratch here — including
  // custom {{goal_diff}}/{{reward}} templates — otherwise reaching a tier
  // that wasn't already met at load time leaves the header blank.
  renderTemplate(template, vars) {
    let html = template;
    if (vars.goal_diff !== undefined) {
      html = html.split('{{goal_diff}}').join(`<span class="highlight milestone-amount">${this.escapeHtml(vars.goal_diff)}</span>`);
    }
    if (vars.reward !== undefined) {
      html = html.split('{{reward}}').join(`<span class="highlight milestone-reward-name">${this.escapeHtml(vars.reward)}</span>`);
    }
    return html;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  updateMessage(total, achievedCount) {
    if (!this.messageEl) return;

    const completed = achievedCount >= this.markers.length;
    this.messageEl.dataset.milestoneState = completed ? 'completed' : 'progress';
    this.dataset.milestoneState = completed ? 'completed' : 'progress';

    if (completed) {
      const current = this.markers[achievedCount - 1];
      if (this.completedTextEl && current) {
        const title = current.el.querySelector('.marker-title');
        const rewardName = title ? title.textContent : '';
        const template = current.el.dataset.achievedMessage || "You've unlocked {{reward}} — it's been added to checkout!";
        this.completedTextEl.innerHTML = this.renderTemplate(template, { reward: rewardName });
      }
      return;
    }

    const next = this.markers[achievedCount];
    const remaining = Math.max(0, next.threshold - total);
    const goalDiff = `₹${Math.ceil(remaining / 100)}`;
    const title = next.el.querySelector('.marker-title');
    const rewardName = title ? title.textContent : '';
    const template = next.el.dataset.unlockMessage || 'Add {{goal_diff}} more to unlock {{reward}}';

    if (this.progressTextEl) {
      this.progressTextEl.innerHTML = this.renderTemplate(template, { goal_diff: goalDiff, reward: rewardName });
    }
  }

  playConfetti() {
    if (window.confetti) {
      window.confetti({ particleCount: 100, spread: 80, origin: { x: 0.9, y: 0.5 }, zIndex: 1999 });
      return;
    }
    if (window.ShippingMilestoneConfettiLoading) return;
    window.ShippingMilestoneConfettiLoading = true;

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
    script.onload = () => {
      if (window.confetti) window.confetti({ particleCount: 100, spread: 80, origin: { x: 0.9, y: 0.5 }, zIndex: 1999 });
    };
    document.head.appendChild(script);
  }
}
customElements.define('shipping-milestone-bar', ShippingMilestoneBar);
